import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getWalletPortfolioCoin } from "../api/endpoints/wallet/wallet";
import {
  getTradingOrdersSimulationFuture,
  getTradingOrdersSimulationLimit,
  getTradingOrdersSimulationMarket,
  getTradingOrdersSimulationSwap,
  getTradingSwapCoins,
  postTradingOrders,
} from "../api/endpoints/trading/trading";
import type { OrderSimulationResult } from "../api/models/orderSimulationResult";
import type { GetTradingOrdersSimulationFutureParams } from "../api/models/getTradingOrdersSimulationFutureParams";
import type { SwapCoin } from "../api/models/swapCoin";
import { getCoins, type Coin, type CoinPair } from "../lib/marketApi";
import { getBearerToken } from "../lib/authClient";
import { toNumber } from "../lib/numberFormat";

import "./TradePage.css";

type Side = "BUY" | "SELL";
type ActiveField = "quote" | "base";
type OrderType = "market" | "limit" | "swap" | "future";

interface LocationState {
  code?: string;
  name?: string;
  icon?: string;
}

const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  market: "Mercado",
  limit: "Limite",
  swap: "Swap",
  future: "Futuros",
};

const parseOrderType = (raw: string | undefined): OrderType => {
  const v = (raw ?? "").toLowerCase();
  if (v === "limit" || v === "swap" || v === "future") return v;
  return "market";
};

const formatPrice = (n: number) =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatQty = (n: number) =>
  n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  });

const formatSignedPct = (n: number) =>
  `${n >= 0 ? "↑ +" : "↓ "}${n.toFixed(2)}% (24h)`;

const authHeaders = () => {
  const token = getBearerToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
};

function Sparkline({ data, isUp }: { data: number[]; isUp: boolean }) {
  if (data.length < 2) return null;
  const W = 320;
  const H = 140;
  const PAD = 4;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((v, i) => {
    const x = PAD + (i / (data.length - 1)) * (W - PAD * 2);
    const y = PAD + (1 - (v - min) / range) * (H - PAD * 2);
    return `${x},${y}`;
  }).join(" ");

  const stroke = isUp ? "#10b981" : "#ef4444";
  const fill = isUp ? "rgba(16,185,129,0.18)" : "rgba(239,68,68,0.18)";
  const firstPt = points.split(" ")[0];
  const area = `M${firstPt} L${points} L${W - PAD},${H} L${PAD},${H} Z`;

  return (
    <svg
      className="trade-chart-svg"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
    >
      <path d={area} fill={fill} />
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function TradePage() {
  const navigate = useNavigate();
  const params = useParams<{ symbol: string; orderType: string }>();
  const location = useLocation();
  const meta = (location.state as LocationState | null) ?? null;
  const symbol = (params.symbol ?? "").toUpperCase();
  const orderType = parseOrderType(params.orderType);
  const hasSide = orderType !== "swap";
  const needsPrice = orderType === "limit";
  const supportsOptionalPrice = orderType === "future";
  const needsLeverage = orderType === "future";

  const [coin, setCoin] = useState<Coin | null>(null);
  const [pair, setPair] = useState<CoinPair | null>(null);
  const [price, setPrice] = useState<number | null>(null);
  const [change24h, setChange24h] = useState<number | null>(null);
  const [chartData, setChartData] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [side, setSide] = useState<Side>("BUY");
  const [quoteAmount, setQuoteAmount] = useState<string>("");
  const [baseAmount, setBaseAmount] = useState<string>("");
  const [limitPrice, setLimitPrice] = useState<string>("");
  const [leverage, setLeverage] = useState<string>("1");
  const [takeProfit, setTakeProfit] = useState<string>("");
  const [stopLoss, setStopLoss] = useState<string>("");
  const [leverageError, setLeverageError] = useState<string | null>(null);
  const [tpslError, setTpslError] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<ActiveField>("quote");
  const [balance, setBalance] = useState<number | null>(null);

  const [simulation, setSimulation] = useState<OrderSimulationResult | null>(
    null,
  );
  const [simLoading, setSimLoading] = useState(false);
  const [simError, setSimError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const [swapFromCoin, setSwapFromCoin] = useState<SwapCoin | null>(null);
  const [swapToCoin, setSwapToCoin] = useState<SwapCoin | null>(null);
  const [swapToCoins, setSwapToCoins] = useState<SwapCoin[]>([]);
  const [swapFromCoins, setSwapFromCoins] = useState<SwapCoin[]>([]);
  const [swapCoinsLoading, setSwapCoinsLoading] = useState(false);
  const [showFromPicker, setShowFromPicker] = useState(false);
  const [showToPicker, setShowToPicker] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const coins = await getCoins();
        if (cancelled) return;

        const found = coins.find((c) =>
          c.pairs.some((p) => p.symbol === symbol)
        );
        if (!found) {
          setError(`Par ${symbol} não encontrado.`);
          return;
        }
        const matchedPair = found.pairs.find((p) => p.symbol === symbol) ??
          null;
        setCoin(found);
        setPair(matchedPair);

        const rawCoin = found as unknown as {
          price?: string;
          chartData?: number[];
          change24hPercent?: number;
        };
        if (rawCoin.price) setPrice(parseFloat(rawCoin.price));
        if (Array.isArray(rawCoin.chartData)) setChartData(rawCoin.chartData);
        if (typeof rawCoin.change24hPercent === "number") {
          setChange24h(rawCoin.change24hPercent);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Erro ao carregar trade page:", err);
        setError(err instanceof Error ? err.message : "Erro desconhecido");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  const effectiveSide: Side = hasSide ? side : "SELL";
  const isBuy = effectiveSide === "BUY";

  const balanceCoin = orderType === "swap"
    ? (swapFromCoin?.coin ?? pair?.baseCoin)
    : (isBuy ? pair?.quoteCoin : pair?.baseCoin);

  useEffect(() => {
    if (!balanceCoin) return;
    let cancelled = false;

    const fetchBalance = async () => {
      try {
        const response = await getWalletPortfolioCoin(
          balanceCoin,
          authHeaders(),
        );
        if (cancelled) return;
        if (response.status === 200 && "data" in response.data) {
          // `available` = saldo de fato livre p/ operar/converter (desconta margem
          // de posições/ordens e o locked de spot). Validar contra ele — não contra
          // `quantity` (saldo bruto), que falharia na Bybit com margem empenhada.
          setBalance(toNumber(response.data.data.available));
        } else {
          setBalance(0);
        }
      } catch (err) {
        if (cancelled) return;
        console.warn("Erro ao buscar saldo:", err);
        setBalance(0);
      }
    };

    fetchBalance();
    return () => {
      cancelled = true;
    };
  }, [balanceCoin]);

  useEffect(() => {
    if (orderType !== "swap" || !pair) return;
    // meta?.code = moeda clicada pelo usuário (pode diferir de pair.baseCoin, ex: USDT→BTCUSDT)
    const initialFromCode = meta?.code ?? pair.baseCoin;
    let cancelled = false;
    const load = async () => {
      setSwapCoinsLoading(true);
      try {
        const toRes = await getTradingSwapCoins(
          { coin: initialFromCode, side: "to" },
          authHeaders(),
        );
        if (cancelled) return;
        if (toRes.status === 200 && "data" in toRes.data) {
          const toCoins = toRes.data.data;
          setSwapToCoins(toCoins);
          const defaultTo = toCoins[0] ?? null;
          setSwapToCoin(defaultTo);

          if (defaultTo) {
            const fromRes = await getTradingSwapCoins(
              { coin: defaultTo.coin, side: "from" },
              authHeaders(),
            );
            if (cancelled) return;
            if (fromRes.status === 200 && "data" in fromRes.data) {
              const fromCoins = fromRes.data.data;
              setSwapFromCoins(fromCoins);
              const initialFrom =
                fromCoins.find((c) => c.coin === initialFromCode) ??
                fromCoins[0] ??
                null;
              setSwapFromCoin(initialFrom);
            }
          }
        }
      } catch (err) {
        if (!cancelled) console.error("Erro ao carregar moedas swap:", err);
      } finally {
        if (!cancelled) setSwapCoinsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [pair?.baseCoin, pair?.quoteCoin, orderType]);

  useEffect(() => {
    if (orderType !== "swap" || !swapFromCoin || swapCoinsLoading) return;
    let cancelled = false;
    const load = async () => {
      try {
        const toRes = await getTradingSwapCoins(
          { coin: swapFromCoin.coin, side: "to" },
          authHeaders(),
        );
        if (cancelled) return;
        if (toRes.status === 200 && "data" in toRes.data) {
          const toCoins = toRes.data.data;
          setSwapToCoins(toCoins);
          setSwapToCoin((prev) => {
            if (prev) {
              const still = toCoins.find((c) => c.coin === prev.coin);
              if (still) return still;
            }
            return toCoins[0] ?? null;
          });
        }
      } catch (err) {
        if (!cancelled) console.error("Erro ao recarregar moedas to:", err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [swapFromCoin?.coin]);

  useEffect(() => {
    if (orderType !== "swap" || !swapToCoin || !showFromPicker) return;
    let cancelled = false;
    const load = async () => {
      try {
        const fromRes = await getTradingSwapCoins(
          { coin: swapToCoin.coin, side: "from" },
          authHeaders(),
        );
        if (cancelled) return;
        if (fromRes.status === 200 && "data" in fromRes.data) {
          setSwapFromCoins(fromRes.data.data);
        }
      } catch (err) {
        if (!cancelled) console.error("Erro ao carregar moedas from:", err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [swapToCoin?.coin, showFromPicker, orderType]);

  const runSimulation = useCallback(
    async (field: ActiveField, value: string) => {
      setLeverageError(null);
      setTpslError(null);
      if (!pair) {
        setSimulation(null);
        setSimError(null);
        return;
      }

      const normalized = value.replace(",", ".");
      const parsedValue = parseFloat(normalized);
      if (!normalized || !Number.isFinite(parsedValue) || parsedValue <= 0) {
        setSimulation(null);
        setSimError(null);
        return;
      }

      // Bloqueia chamada à API se valor está fora dos limites do par
      if (orderType === "swap") {
        if (field === "quote" && swapFromCoin) {
          const min = parseFloat(swapFromCoin.singleFromMinLimit);
          const max = parseFloat(swapFromCoin.singleFromMaxLimit);
          if (parsedValue < min || (max > 0 && parsedValue > max)) {
            setSimulation(null);
            setSimError(null);
            return;
          }
        }
        if (field === "base" && swapToCoin) {
          const min = parseFloat(swapToCoin.singleToMinLimit);
          const max = parseFloat(swapToCoin.singleToMaxLimit);
          if (parsedValue < min || (max > 0 && parsedValue > max)) {
            setSimulation(null);
            setSimError(null);
            return;
          }
        }
      }

      const buying = isBuy;
      const inputCurrency = orderType === "swap"
        ? (swapFromCoin?.coin ?? pair.baseCoin)
        : (buying ? pair.quoteCoin : pair.baseCoin);
      const outputCurrency = orderType === "swap"
        ? (swapToCoin?.coin ?? pair.quoteCoin)
        : (buying ? pair.baseCoin : pair.quoteCoin);

      // swap: "quote" field = "quanto pagar" = inputCurrency (from)
      //       "base"  field = "quanto receber" = outputCurrency (to)
      const typedIsInput = orderType === "swap"
        ? field === "quote"
        : (buying && field === "quote") || (!buying && field === "base");

      setSimLoading(true);
      setSimError(null);
      try {
        let result: OrderSimulationResult | null = null;

        if (orderType === "market") {
          const q: {
            inputCurrency: string;
            outputCurrency: string;
            side: Side;
            inputAmount?: string;
            outputAmount?: string;
          } = { inputCurrency, outputCurrency, side };
          if (typedIsInput) q.inputAmount = normalized;
          else q.outputAmount = normalized;
          const response = await getTradingOrdersSimulationMarket(
            q,
            authHeaders(),
          );
          if (response.status === 200 && "data" in response.data) {
            result = response.data.data;
          } else {
            throw new Error(
              (response.data as { message?: string })?.message ??
                `Falha na simulação (status ${response.status}).`,
            );
          }
        } else if (orderType === "limit") {
          const priceNormalized = limitPrice.replace(",", ".");
          const priceNum = parseFloat(priceNormalized);
          if (!priceNormalized || !Number.isFinite(priceNum) || priceNum <= 0) {
            setSimulation(null);
            setSimError("Informe o preço limite.");
            return;
          }
          let qtyNormalized: string;
          if (field === "base") {
            qtyNormalized = normalized;
          } else {
            const qtyFromQuote = parsedValue / priceNum;
            if (!Number.isFinite(qtyFromQuote) || qtyFromQuote <= 0) {
              setSimulation(null);
              setSimError(null);
              return;
            }
            qtyNormalized = qtyFromQuote.toString();
          }
          const response = await getTradingOrdersSimulationLimit(
            {
              inputCurrency,
              outputCurrency,
              side,
              qty: qtyNormalized,
              price: priceNormalized,
            },
            authHeaders(),
          );
          if (response.status === 200 && "data" in response.data) {
            result = response.data.data;
          } else {
            throw new Error(
              (response.data as { message?: string })?.message ??
                `Falha na simulação (status ${response.status}).`,
            );
          }
        } else if (orderType === "swap") {
          if (!swapFromCoin || !swapToCoin) {
            setSimulation(null);
            setSimError(null);
            return;
          }
          const q: {
            inputCurrency: string;
            outputCurrency: string;
            inputAmount?: string;
            outputAmount?: string;
          } = {
            inputCurrency: swapFromCoin.coin,
            outputCurrency: swapToCoin.coin,
          };
          if (typedIsInput) q.inputAmount = normalized;
          else q.outputAmount = normalized;
          const response = await getTradingOrdersSimulationSwap(
            q,
            authHeaders(),
          );
          if (response.status === 200 && "data" in response.data) {
            result = response.data.data;
          } else {
            throw new Error(
              (response.data as { message?: string })?.message ??
                `Falha na simulação (status ${response.status}).`,
            );
          }
        } else {
          // future — contrato novo (linear): o par vai em `symbol` (base+quote,
          // ex: BTC/USDC → "BTCUSDC") e o valor digitado é a MARGEM
          // (`marginAmount`), único campo de valor. Não há mais entrada por
          // quantidade. A exposição/notional = margem × leverage é calculada
          // pelo backend e devolvida em `linear.notional`.
          const futureSymbol = `${pair.baseCoin}${pair.quoteCoin}`;
          const leverageNormalized = leverage.replace(",", ".");
          const leverageNum = parseFloat(leverageNormalized);
          if (
            !leverageNormalized || !Number.isFinite(leverageNum) ||
            leverageNum <= 0
          ) {
            setSimulation(null);
            setSimError("Informe a alavancagem.");
            return;
          }
          const priceNormalized = limitPrice.replace(",", ".");
          const hasLimitPrice = priceNormalized &&
            Number.isFinite(parseFloat(priceNormalized)) &&
            parseFloat(priceNormalized) > 0;
          // Validação client-side de TP/SL (espelha E_INVALID_TPSL do backend).
          // Referência: preço limite quando informado; senão o preço de mercado.
          const tpNormalized = takeProfit.replace(",", ".");
          const slNormalized = stopLoss.replace(",", ".");
          const tpNum = parseFloat(tpNormalized);
          const slNum = parseFloat(slNormalized);
          const hasTp = !!tpNormalized && Number.isFinite(tpNum) && tpNum > 0;
          const hasSl = !!slNormalized && Number.isFinite(slNum) && slNum > 0;
          const refPrice = hasLimitPrice ? parseFloat(priceNormalized) : price;
          if (refPrice !== null && refPrice > 0) {
            if (hasTp && (buying ? tpNum <= refPrice : tpNum >= refPrice)) {
              setSimulation(null);
              setTpslError(
                buying
                  ? "Take profit deve ficar acima do preço de entrada (long)."
                  : "Take profit deve ficar abaixo do preço de entrada (short).",
              );
              return;
            }
            if (hasSl && (buying ? slNum >= refPrice : slNum <= refPrice)) {
              setSimulation(null);
              setTpslError(
                buying
                  ? "Stop loss deve ficar abaixo do preço de entrada (long)."
                  : "Stop loss deve ficar acima do preço de entrada (short).",
              );
              return;
            }
          }

          // `normalized` = valor do campo "Margem" (marginAmount), o único
          // campo de valor enviado. `symbol` carrega base+quote, eliminando o
          // antigo par inputCurrency/outputCurrency.
          const futureParams: GetTradingOrdersSimulationFutureParams = {
            symbol: futureSymbol,
            // A simulação de futuros usa LONG/SHORT (não BUY/SELL)
            side: buying ? "LONG" : "SHORT",
            marginAmount: normalized,
            leverage: leverageNormalized,
          };
          if (hasLimitPrice) futureParams.price = priceNormalized;
          if (hasTp) futureParams.takeProfit = tpNormalized;
          if (hasSl) futureParams.stopLoss = slNormalized;
          const response = await getTradingOrdersSimulationFuture(
            futureParams,
            authHeaders(),
          );
          if (response.status === 200 && "data" in response.data) {
            result = response.data.data;
          } else {
            const body = response.data as {
              message?: string;
              error?: { code?: string };
            };
            const code = body?.error?.code;
            if (
              code === "E_INVALID_LEVERAGE" ||
              code === "E_LEVERAGE_OUT_OF_RANGE"
            ) {
              // A mensagem do backend traz o range válido de alavancagem
              setSimulation(null);
              setLeverageError(body?.message ?? "Alavancagem inválida.");
              return;
            }
            if (code === "E_INVALID_TPSL") {
              setSimulation(null);
              setTpslError(body?.message ?? "Take profit / stop loss inválido.");
              return;
            }
            if (code === "E_QTY_BELOW_MIN") {
              // Margem (× leverage) gera quantidade abaixo do mínimo do
              // contrato. A mensagem do backend traz a qty mínima.
              setSimulation(null);
              setSimError(
                body?.message ??
                  "Margem abaixo do mínimo do contrato. Aumente a margem ou a alavancagem.",
              );
              return;
            }
            if (code === "E_LINEAR_PAIR_NOT_FOUND") {
              throw new Error("Par indisponível para futuros.");
            }
            if (code === "E_LINEAR_TICKER_UNAVAILABLE") {
              throw new Error("Cotação indisponível, tente novamente.");
            }
            throw new Error(
              body?.message ??
                `Falha na simulação (status ${response.status}).`,
            );
          }
        }

        if (!result) {
          setSimulation(null);
          return;
        }

        setSimulation(result);

        const isQuoteField = field === "quote";
        if (result.linear) {
          // Futuros: qty (baseCoin) e notional (quoteCoin) vêm direto do
          // objeto linear — sem condicionar ao side.
          if (isQuoteField) setBaseAmount(result.linear.qty);
          else setQuoteAmount(result.linear.notional);
        } else if (orderType === "swap") {
          // quote = paying (from) → fill receiving (to) with outputAmount
          // base  = receiving (to) → fill paying (from) with inputAmount
          if (isQuoteField) setBaseAmount(result.outputAmount);
          else setQuoteAmount(result.inputAmount);
        } else {
          const oppositeValue = buying
            ? (isQuoteField ? result.outputAmount : result.inputAmount)
            : (isQuoteField ? result.inputAmount : result.outputAmount);
          if (isQuoteField) setBaseAmount(oppositeValue);
          else setQuoteAmount(oppositeValue);
        }
      } catch (err) {
        console.error("Erro na simulação:", err);
        setSimulation(null);
        setSimError(err instanceof Error ? err.message : "Erro desconhecido");
      } finally {
        setSimLoading(false);
      }
    },
    [
      pair,
      side,
      isBuy,
      orderType,
      limitPrice,
      leverage,
      takeProfit,
      stopLoss,
      price,
      swapFromCoin,
      swapToCoin,
    ],
  );

  const activeValue = activeField === "quote" ? quoteAmount : baseAmount;

  useEffect(() => {
    if (!pair) return;
    const delay = orderType === "swap" ? 600 : 400;
    const handle = window.setTimeout(() => {
      runSimulation(activeField, activeValue);
    }, delay);
    return () => window.clearTimeout(handle);
  }, [activeField, activeValue, pair, side, orderType, runSimulation]);

  useEffect(() => {
    const handle = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(handle);
  }, []);

  const expiresAt = simulation
    ? new Date(simulation.quoteExpiresIn).getTime()
    : null;
  const countdown = expiresAt !== null
    ? Math.max(0, Math.floor((expiresAt - now) / 1000))
    : null;

  const refreshingRef = useRef(false);
  useEffect(() => {
    if (!simulation || countdown === null) return;
    if (countdown > 3) {
      refreshingRef.current = false;
      return;
    }
    if (refreshingRef.current) return;
    refreshingRef.current = true;
    runSimulation(activeField, activeValue);
  }, [countdown, simulation, activeField, activeValue, runSimulation]);

  const isUp = (change24h ?? 0) >= 0;
  const actionCoinLabel = pair?.baseCoin ?? coin?.code ?? "";

  // Preview de exposição (notional) = margem × alavancagem. Valor indicativo;
  // o notional definitivo vem do backend em `simulation.linear.notional`.
  const futureExposure = useMemo(() => {
    if (orderType !== "future") return null;
    const m = parseFloat(quoteAmount.replace(",", "."));
    const l = parseFloat(leverage.replace(",", "."));
    if (!Number.isFinite(m) || !Number.isFinite(l) || m <= 0 || l <= 0) {
      return null;
    }
    return m * l;
  }, [orderType, quoteAmount, leverage]);

  const supportsOutputAmount = orderType === "swap" &&
    Number(swapToCoin?.singleToMaxLimit ?? "0") > 0;

  // Força campo ativo de volta para "quote" quando outputAmount não é suportado
  useEffect(() => {
    if (orderType === "swap" && !supportsOutputAmount && activeField === "base") {
      setActiveField("quote");
    }
  }, [supportsOutputAmount, orderType, activeField]);

  const swapValidationError = (() => {
    if (orderType !== "swap") return null;
    if (activeField === "quote" && quoteAmount && swapFromCoin) {
      const val = parseFloat(quoteAmount.replace(",", "."));
      if (Number.isFinite(val) && val > 0) {
        const min = parseFloat(swapFromCoin.singleFromMinLimit);
        const max = parseFloat(swapFromCoin.singleFromMaxLimit);
        if (val < min) return `Mínimo: ${swapFromCoin.singleFromMinLimit} ${swapFromCoin.coin}`;
        if (max > 0 && val > max) return `Máximo: ${swapFromCoin.singleFromMaxLimit} ${swapFromCoin.coin}`;
      }
    }
    if (activeField === "base" && baseAmount && swapToCoin && supportsOutputAmount) {
      const val = parseFloat(baseAmount.replace(",", "."));
      if (Number.isFinite(val) && val > 0) {
        const min = parseFloat(swapToCoin.singleToMinLimit);
        const max = parseFloat(swapToCoin.singleToMaxLimit);
        if (val < min) return `Mínimo: ${swapToCoin.singleToMinLimit} ${swapToCoin.coin}`;
        if (max > 0 && val > max) return `Máximo: ${swapToCoin.singleToMaxLimit} ${swapToCoin.coin}`;
      }
    }
    return null;
  })();

  const limitDecimals = (v: string, maxDecimals: number): string => {
    const sepIdx = Math.max(v.lastIndexOf("."), v.lastIndexOf(","));
    if (sepIdx === -1) return v;
    return v.slice(0, sepIdx + 1) + v.slice(sepIdx + 1, sepIdx + 1 + maxDecimals);
  };

  /**
   * Trunca (nunca arredonda para cima) para não estourar o saldo no 100%.
   * A casa extra do `toFixed` absorve o erro de ponto flutuante antes do corte.
   */
  const truncateToDecimals = (value: number, decimals: number): string => {
    const fixed = value.toFixed(decimals + 2);
    const dotIndex = fixed.indexOf(".");
    const truncated = decimals > 0
      ? fixed.slice(0, dotIndex + 1 + decimals)
      : fixed.slice(0, dotIndex);
    return truncated.replace(/\.?0+$/, "") || "0";
  };

  const handleQuickSelect = (pct: number) => {
    if (balance === null) return;
    const value = balance * (pct / 100);
    const decimals = orderType === "swap" && swapFromCoin
      ? swapFromCoin.accuracyLength
      : 8;
    const str = truncateToDecimals(value, decimals);

    // Na venda spot o saldo é da moeda base (ex.: SOL), então o percentual
    // preenche "Quantidade". Compra, margem de futuros e swap gastam a moeda
    // do campo de cima ("Valor a pagar"/"Margem"/"Quanto pagar").
    const fillsBaseField = orderType !== "swap" && orderType !== "future" &&
      !isBuy;

    if (fillsBaseField) {
      setActiveField("base");
      setBaseAmount(str);
      return;
    }

    setActiveField("quote");
    setQuoteAmount(str);
  };

  const handleQuoteChange = (raw: string) => {
    let v = raw.replace(/[^\d.,]/g, "");
    if (orderType === "swap" && swapFromCoin) {
      v = limitDecimals(v, swapFromCoin.accuracyLength);
    }
    setActiveField("quote");
    setQuoteAmount(v);
  };

  const handleBaseChange = (raw: string) => {
    if (orderType === "swap" && !supportsOutputAmount) return;
    let v = raw.replace(/[^\d.,]/g, "");
    if (orderType === "swap" && swapToCoin) {
      v = limitDecimals(v, swapToCoin.accuracyLength);
    }
    setActiveField("base");
    setBaseAmount(v);
  };

  const handleLimitPriceChange = (raw: string) => {
    const v = raw.replace(/[^\d.,]/g, "");
    setLimitPrice(v);
  };

  const handleLeverageChange = (raw: string) => {
    const v = raw.replace(/[^\d.,]/g, "");
    setLeverage(v);
  };

  const handleTakeProfitChange = (raw: string) => {
    const v = raw.replace(/[^\d.,]/g, "");
    setTakeProfit(v);
  };

  const handleStopLossChange = (raw: string) => {
    const v = raw.replace(/[^\d.,]/g, "");
    setStopLoss(v);
  };

  const handleSubmit = async () => {
    if (!simulation) return;
    setConfirming(true);
    setConfirmError(null);
    try {
      const response = await postTradingOrders(
        { quoteId: simulation.quoteId },
        authHeaders(),
      );
      if (response.status >= 200 && response.status < 300) {
        const order = "data" in response.data
          ? (response.data as {
            data?: {
              historyId?: string;
              orderId?: string;
              orderStatus?: string;
              product?: string;
            };
          }).data
          : undefined;
        if (order?.product === "FUTURE") {
          // FILLED ⇒ posição aberta (detalhe); NEW/PARTIALLY_FILLED ⇒ lista
          if (order.orderStatus === "FILLED" && order.orderId) {
            navigate(`/futures/positions/${order.orderId}`, {
              replace: true,
              state: { toast: { type: "success", message: "Posição aberta" } },
            });
          } else {
            navigate("/futures", {
              replace: true,
              state: { toast: { type: "success", message: "Ordem criada" } },
            });
          }
        } else if (order?.historyId) {
          navigate(`/wallet/history/${order.historyId}`, { replace: true });
        } else {
          navigate("/orders", { replace: true });
        }
      } else {
        const body = response.data as {
          message?: string;
          error?: { code?: string };
        };
        const code = body?.error?.code;
        let msg: string;
        if (code === "E_QUOTE_EXPIRED" || code === "E_SWAP_QUOTE_EXPIRED") {
          msg = "Simulação expirou. Atualizando cotação...";
          // Re-simula automaticamente para gerar um novo quoteId
          runSimulation(activeField, activeValue);
        } else if (code === "E_BYBIT_SUBACCOUNT_MISSING") {
          msg = "Conta de trading não configurada. Contate o suporte.";
        } else {
          msg = body?.message ?? `Falha ao confirmar (status ${response.status}).`;
        }
        setConfirmError(msg);
      }
    } catch (err) {
      console.error("Erro ao confirmar ordem:", err);
      setConfirmError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setConfirming(false);
    }
  };

  const parsedActiveAmount = useMemo(() => {
    const n = parseFloat(activeValue.replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  }, [activeValue]);

  const paying = useMemo(() => {
    if (!simulation) return null;
    return { amount: simulation.inputAmount, coin: simulation.inputCurrency };
  }, [simulation]);

  const receiving = useMemo(() => {
    if (!simulation) return null;
    return {
      amount: simulation.outputAmount,
      coin: simulation.outputCurrency,
    };
  }, [simulation]);

  const quoteStillValid =
    (orderType !== "swap" && orderType !== "future") ||
    countdown === null || countdown > 0;
  const canConfirm = !!simulation && !simLoading && !confirming &&
    parsedActiveAmount > 0 && balance !== null && quoteStillValid;

  const confirmCta = orderType === "swap"
    ? "Confirmar swap"
    : orderType === "future"
    ? `Confirmar ${isBuy ? "long" : "short"}`
    : `Confirmar ${isBuy ? "compra" : "venda"}`;

  const submitPreviewCta = orderType === "swap"
    ? `Converter ${actionCoinLabel}`
    : orderType === "future"
    ? `${isBuy ? "Long" : "Short"} ${actionCoinLabel}`
    : `${isBuy ? "Comprar" : "Vender"} ${actionCoinLabel}`;

  return (
    <div className="page-centered">
      <div className="trade-wrap">
        <div className="trade-header">
          <button
            type="button"
            className="trade-iconBtn"
            onClick={() => navigate(-1)}
            aria-label="Voltar"
          >
            ←
          </button>
          <div className="trade-pair">
            <span>
              {pair ? `${pair.baseCoin}/${pair.quoteCoin}` : symbol}
            </span>
            <span className="trade-pair-chevron">⌄</span>
          </div>
          <button
            type="button"
            className="trade-iconBtn trade-iconBtn--bot"
            onClick={() => navigate("/chat")}
            aria-label="Chat IA"
          >
            🤖
          </button>
        </div>

        <div className="trade-modeBadge">{ORDER_TYPE_LABELS[orderType]}</div>

        {loading && <div className="trade-status">Carregando par...</div>}

        {error && !loading && (
          <div className="trade-status trade-status--error">{error}</div>
        )}

        {!loading && !error && pair && (
          <>
            {orderType !== "swap" && <div className="trade-card trade-chartCard">
              <div className="trade-chart-head">
                <div className="trade-chart-label">Gráfico</div>
                <div>
                  <div className="trade-chart-price">
                    {price !== null
                      ? `$${formatPrice(price)} ${pair.quoteCoin}`
                      : "—"}
                  </div>
                  {change24h !== null && (
                    <div
                      className={`trade-chart-change ${
                        isUp
                          ? "trade-chart-change--up"
                          : "trade-chart-change--down"
                      }`}
                    >
                      {formatSignedPct(change24h)}
                    </div>
                  )}
                </div>
              </div>
              {chartData.length > 1
                ? <Sparkline data={chartData} isUp={isUp} />
                : <div className="trade-chart-empty">Gráfico indisponível</div>}
            </div>}

            {hasSide && (
              <div className="trade-card trade-toggle">
                <button
                  type="button"
                  className={`trade-toggleBtn ${
                    isBuy ? "trade-toggleBtn--active" : ""
                  }`}
                  onClick={() => setSide("BUY")}
                >
                  {orderType === "future" ? "Long" : "Comprar"}
                </button>
                <button
                  type="button"
                  className={`trade-toggleBtn ${
                    !isBuy ? "trade-toggleBtn--activeSell" : ""
                  }`}
                  onClick={() => setSide("SELL")}
                >
                  {orderType === "future" ? "Short" : "Vender"}
                </button>
              </div>
            )}

            <div className="trade-card">
              {needsPrice && (
                <>
                  <div className="trade-fieldLabel">Preço limite</div>
                  <div className="trade-amountInput">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0,00"
                      value={limitPrice}
                      onChange={(e) => handleLimitPriceChange(e.target.value)}
                    />
                    <span className="trade-amountInput-coin">
                      {pair.quoteCoin}
                    </span>
                  </div>
                </>
              )}

              {orderType === "swap" && (
                <>
                  <div className="trade-balance">
                    <span>Saldo disponível:</span>
                    <span className="trade-balance-value">
                      {balance !== null ? formatQty(balance) : "—"}
                    </span>
                    <span>{balanceCoin ?? ""}</span>
                  </div>
                </>
              )}

              <div
                className={`trade-fieldLabel ${
                  needsPrice || orderType === "swap"
                    ? "trade-fieldLabel--spaced"
                    : ""
                }`}
              >
                {orderType === "swap"
                  ? "Quanto pagar"
                  : orderType === "future"
                  ? "Margem"
                  : "Valor a pagar"}
              </div>
              <div className="trade-amountInput">
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={quoteAmount}
                  onChange={(e) => handleQuoteChange(e.target.value)}
                />
                {orderType === "swap" ? (
                  <button
                    type="button"
                    className="trade-coinPickerBtn"
                    onClick={() => {
                      setShowFromPicker((v) => !v);
                      setShowToPicker(false);
                    }}
                  >
                    {swapFromCoin?.icon && (
                      <img
                        src={swapFromCoin.icon}
                        alt={swapFromCoin.coin}
                        className="trade-coinPickerBtn-icon"
                      />
                    )}
                    {swapFromCoin?.coin ?? pair.baseCoin}
                    <span className="trade-coinPickerBtn-chevron">⌄</span>
                  </button>
                ) : (
                  <span className="trade-amountInput-coin">{pair.quoteCoin}</span>
                )}
              </div>

              {orderType !== "swap" && (
                <div className="trade-balance">
                  <span>Saldo disponível:</span>
                  <span className="trade-balance-value">
                    {balance !== null ? formatQty(balance) : "—"}
                  </span>
                  <span>{balanceCoin ?? ""}</span>
                </div>
              )}

              {orderType !== "swap" && (
                <div className="trade-quickGrid">
                  {[25, 50, 100].map((pct) => (
                    <button
                      key={pct}
                      type="button"
                      className="trade-quickBtn"
                      onClick={() => handleQuickSelect(pct)}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>
              )}

              {orderType === "swap" && showFromPicker && (
                <div className="trade-coinPicker">
                  {swapFromCoins.length === 0
                    ? <div className="trade-coinPicker-empty">Carregando...</div>
                    : swapFromCoins.map((c) => (
                      <button
                        key={c.coin}
                        type="button"
                        className={`trade-coinPickerItem ${
                          swapFromCoin?.coin === c.coin
                            ? "trade-coinPickerItem--active"
                            : ""
                        }`}
                        onClick={() => {
                          setSwapFromCoin(c);
                          setShowFromPicker(false);
                          setSimulation(null);
                          setQuoteAmount("");
                          setBaseAmount("");
                        }}
                      >
                        {c.icon && (
                          <img
                            src={c.icon}
                            alt={c.coin}
                            className="trade-coinPickerItem-icon"
                          />
                        )}
                        <span className="trade-coinPickerItem-code">{c.coin}</span>
                        <span className="trade-coinPickerItem-name">{c.name}</span>
                      </button>
                    ))}
                </div>
              )}

              {orderType === "swap" && (
                <div className="trade-quickGrid" style={{ marginTop: "0.75rem" }}>
                  {[25, 50, 100].map((pct) => (
                    <button
                      key={pct}
                      type="button"
                      className="trade-quickBtn"
                      onClick={() => handleQuickSelect(pct)}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>
              )}

              {/* Futuros não tem entrada por quantidade: o valor é só a margem */}
              {orderType !== "future" && (
                <>
                  <div className="trade-fieldLabel trade-fieldLabel--spaced">
                    {orderType === "swap" ? "Quanto receber" : "Quantidade"}
                  </div>
                  <div className={`trade-amountInput ${
                    orderType === "swap" && !supportsOutputAmount
                      ? "trade-amountInput--disabled"
                      : ""
                  }`}>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder={
                        orderType === "swap" && !supportsOutputAmount
                          ? "Preenchido automaticamente"
                          : "0,00000000"
                      }
                      value={baseAmount}
                      readOnly={orderType === "swap" && !supportsOutputAmount}
                      onChange={(e) => handleBaseChange(e.target.value)}
                    />
                    {orderType === "swap" ? (
                      <button
                        type="button"
                        className="trade-coinPickerBtn"
                        onClick={() => {
                          setShowToPicker((v) => !v);
                          setShowFromPicker(false);
                        }}
                      >
                        {swapToCoin?.icon && (
                          <img
                            src={swapToCoin.icon}
                            alt={swapToCoin.coin}
                            className="trade-coinPickerBtn-icon"
                          />
                        )}
                        {swapToCoin?.coin ?? pair.quoteCoin}
                        <span className="trade-coinPickerBtn-chevron">⌄</span>
                      </button>
                    ) : (
                      <span className="trade-amountInput-coin">
                        {pair.baseCoin}
                      </span>
                    )}
                  </div>
                </>
              )}

              {orderType === "swap" && showToPicker && (
                <div className="trade-coinPicker">
                  {swapToCoins.length === 0
                    ? <div className="trade-coinPicker-empty">Carregando...</div>
                    : swapToCoins.map((c) => (
                      <button
                        key={c.coin}
                        type="button"
                        className={`trade-coinPickerItem ${
                          swapToCoin?.coin === c.coin
                            ? "trade-coinPickerItem--active"
                            : ""
                        }`}
                        onClick={() => {
                          setSwapToCoin(c);
                          setShowToPicker(false);
                          setSimulation(null);
                          setBaseAmount("");
                        }}
                      >
                        {c.icon && (
                          <img
                            src={c.icon}
                            alt={c.coin}
                            className="trade-coinPickerItem-icon"
                          />
                        )}
                        <span className="trade-coinPickerItem-code">{c.coin}</span>
                        <span className="trade-coinPickerItem-name">{c.name}</span>
                      </button>
                    ))}
                </div>
              )}

              {needsLeverage && (
                <>
                  <div className="trade-fieldLabel trade-fieldLabel--spaced">
                    Alavancagem
                  </div>
                  <div className="trade-amountInput">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="1"
                      value={leverage}
                      onChange={(e) => handleLeverageChange(e.target.value)}
                    />
                    <span className="trade-amountInput-coin">x</span>
                  </div>
                  {leverageError && (
                    <div className="trade-fieldError">{leverageError}</div>
                  )}
                  <div className="trade-quickGrid">
                    {[1, 5, 10, 20].map((lev) => (
                      <button
                        key={lev}
                        type="button"
                        className="trade-quickBtn"
                        onClick={() => setLeverage(String(lev))}
                      >
                        {lev}x
                      </button>
                    ))}
                  </div>

                  <div className="trade-balance">
                    <span>Exposição:</span>
                    <span className="trade-balance-value">
                      {futureExposure !== null
                        ? formatPrice(futureExposure)
                        : "—"}
                    </span>
                    <span>{pair.quoteCoin}</span>
                  </div>

                  {supportsOptionalPrice && (
                    <>
                      <div className="trade-fieldLabel trade-fieldLabel--spaced">
                        Preço limite (opcional)
                      </div>
                      <div className="trade-amountInput">
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="A mercado se vazio"
                          value={limitPrice}
                          onChange={(e) =>
                            handleLimitPriceChange(e.target.value)}
                        />
                        <span className="trade-amountInput-coin">
                          {pair.quoteCoin}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="trade-fieldLabel trade-fieldLabel--spaced">
                    Take profit (opcional)
                  </div>
                  <div className="trade-amountInput">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder={isBuy
                        ? "Acima do preço de entrada"
                        : "Abaixo do preço de entrada"}
                      value={takeProfit}
                      onChange={(e) => handleTakeProfitChange(e.target.value)}
                    />
                    <span className="trade-amountInput-coin">
                      {pair.quoteCoin}
                    </span>
                  </div>

                  <div className="trade-fieldLabel trade-fieldLabel--spaced">
                    Stop loss (opcional)
                  </div>
                  <div className="trade-amountInput">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder={isBuy
                        ? "Abaixo do preço de entrada"
                        : "Acima do preço de entrada"}
                      value={stopLoss}
                      onChange={(e) => handleStopLossChange(e.target.value)}
                    />
                    <span className="trade-amountInput-coin">
                      {pair.quoteCoin}
                    </span>
                  </div>
                  {tpslError && (
                    <div className="trade-fieldError">{tpslError}</div>
                  )}
                </>
              )}
            </div>

            {orderType === "swap" && swapCoinsLoading && (
              <div className="trade-status">Carregando moedas disponíveis...</div>
            )}

            {swapValidationError && (
              <div className="trade-status trade-status--error">
                {swapValidationError}
              </div>
            )}

            {simLoading && !simulation && !swapValidationError && (
              <div className="trade-status">Simulando ordem...</div>
            )}

            {simError && !simLoading && !swapValidationError && (
              <div className="trade-status trade-status--error">{simError}</div>
            )}

            {simulation && (
              <>
                <div className="trade-summary">
                  <div className="trade-summary-head">
                    <span className="trade-summary-title">
                      Resumo da operação
                    </span>
                    {countdown !== null && (
                      <span className="trade-summary-countdown">
                        {simLoading ? "atualizando..." : `${countdown}s`}
                      </span>
                    )}
                  </div>
                  <div className="trade-summary-row">
                    <span>Par</span>
                    <span>{simulation.pairName}</span>
                  </div>
                  <div className="trade-summary-row">
                    <span>Tipo de ordem</span>
                    <span>{ORDER_TYPE_LABELS[orderType]}</span>
                  </div>
                  {hasSide && (
                    <div className="trade-summary-row">
                      <span>Operação</span>
                      <span>
                        {orderType === "future"
                          ? (isBuy ? "Long" : "Short")
                          : (isBuy ? "Compra" : "Venda")}
                      </span>
                    </div>
                  )}
                  {needsLeverage && !simulation.linear && (
                    <div className="trade-summary-row">
                      <span>Alavancagem</span>
                      <span>{leverage || "1"}x</span>
                    </div>
                  )}
                  {simulation.linear && (
                    <div className="trade-summary-row">
                      <span>Alavancagem</span>
                      <span>
                        {simulation.linear.leverage}x ·{" "}
                        {simulation.linear.marginMode}
                      </span>
                    </div>
                  )}
                  {!simulation.linear && (
                    <>
                      <div className="trade-summary-row">
                        <span>Valor a pagar</span>
                        <span>
                          {paying ? `${paying.amount} ${paying.coin}` : "—"}
                        </span>
                      </div>
                      <div className="trade-summary-row">
                        <span>Quantidade estimada</span>
                        <span>
                          {receiving
                            ? `≈ ${receiving.amount} ${receiving.coin}`
                            : "—"}
                        </span>
                      </div>
                    </>
                  )}
                  {simulation.linear && (
                    <>
                      <div className="trade-summary-row">
                        <span>Quantidade</span>
                        <span>
                          {simulation.linear.qty} {pair.baseCoin}
                        </span>
                      </div>
                      <div className="trade-summary-row">
                        <span>Exposição</span>
                        <span>
                          {simulation.linear.notional}{" "}
                          {simulation.linear.marginCurrency}
                        </span>
                      </div>
                      <div className="trade-summary-row">
                        <span>Margem necessária</span>
                        <span>
                          {simulation.linear.initialMargin}{" "}
                          {simulation.linear.marginCurrency}
                        </span>
                      </div>
                      <div className="trade-summary-row">
                        <span>Entrada estimada</span>
                        <span>
                          {simulation.linear.entryPrice} {pair.quoteCoin}
                        </span>
                      </div>
                      <div className="trade-summary-row trade-summary-row--alert">
                        <span>Liquidação estimada</span>
                        <span>
                          {/* Long: liq abaixo do entry (−); short: acima (+) */}
                          {simulation.linear.liqPrice} (
                          {simulation.linear.positionSide === "LONG"
                            ? "−"
                            : "+"}
                          {simulation.linear.liqPricePercent}% do entry)
                        </span>
                      </div>
                      {simulation.linear.takeProfit && (
                        <div className="trade-summary-row">
                          <span>Take profit</span>
                          <span>
                            {simulation.linear.takeProfit} {pair.quoteCoin}
                          </span>
                        </div>
                      )}
                      {simulation.linear.stopLoss && (
                        <div className="trade-summary-row">
                          <span>Stop loss</span>
                          <span>
                            {simulation.linear.stopLoss} {pair.quoteCoin}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="trade-summary-row">
                    <span>Preço {needsPrice ? "limite" : "base"}</span>
                    <span>
                      {simulation.basePrice} {pair.quoteCoin}
                    </span>
                  </div>
                  <div className="trade-summary-row">
                    <span>Taxa estimada</span>
                    <span>
                      {simulation.linear
                        ? `${simulation.fee} ${simulation.linear.marginCurrency} (${simulation.feePercent}%)`
                        : `${simulation.feePercent}%`}
                    </span>
                  </div>
                </div>

                {simulation.linear ? (
                  <div className="trade-important trade-important--danger">
                    <span className="trade-important-icon" aria-hidden="true">
                      ⚠
                    </span>
                    <div>
                      <div className="trade-important-title">
                        Risco de alavancagem
                      </div>
                      <div className="trade-important-text">
                        Operações alavancadas amplificam ganhos e perdas. Se o
                        preço{" "}
                        {simulation.linear.positionSide === "LONG"
                          ? "cair até"
                          : "subir até"}{" "}
                        {simulation.linear.liqPrice}{" "}
                        {pair.quoteCoin}, a posição será liquidada e a margem de{" "}
                        {simulation.linear.initialMargin}{" "}
                        {simulation.linear.marginCurrency} será perdida.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="trade-important">
                    <span className="trade-important-icon" aria-hidden="true">
                      ⓘ
                    </span>
                    <div>
                      <div className="trade-important-title">Importante</div>
                      <div className="trade-important-text">
                        {needsPrice
                          ? "A ordem só será executada quando o preço de mercado atingir o valor definido."
                          : "A quantidade final pode variar ligeiramente conforme o preço de execução no momento da ordem."}
                      </div>
                    </div>
                  </div>
                )}

                {confirmError && (
                  <div className="trade-status trade-status--error">
                    {confirmError}
                  </div>
                )}

                <div className="trade-actions">
                  <button
                    type="button"
                    className={`trade-btn trade-btn--confirm ${
                      isBuy ? "" : "trade-btn--confirmSell"
                    }`}
                    onClick={handleSubmit}
                    disabled={!canConfirm}
                  >
                    {confirming
                      ? "Confirmando..."
                      : orderType === "future" && countdown !== null
                      ? `${confirmCta} (${countdown}s)`
                      : confirmCta}
                  </button>
                  <button
                    type="button"
                    className="trade-btn trade-btn--cancel"
                    onClick={() => navigate(-1)}
                    disabled={confirming}
                  >
                    Cancelar
                  </button>
                </div>
              </>
            )}

            {!simulation && !simLoading && !simError && (
              <button
                type="button"
                className={`trade-submitBtn ${
                  isBuy ? "trade-submitBtn--buy" : "trade-submitBtn--sell"
                }`}
                disabled
              >
                {submitPreviewCta}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
